import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  createTaxConfiguration,
  disableTaxConfiguration,
  getTaxConfigurations,
} from "@/services/adminService"; // Assuming the service functions are correct

// Import the shared helpers
import {
  getDefaultEpfForm,
  getDefaultEsiForm,
  mapEpfFromServer,
  mapEsiFromServer,
} from "@/components/admin/tax-configs/TaxHelpers";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Import the new components
import EpfConfiguration from "@/components/admin/tax-configs/EpfConfiguration";
import EsiConfiguration from "@/components/admin/tax-configs/EsiConfiguration";
import ProfessionalTaxConfiguration from "@/components/admin/tax-configs/ProfessionalTaxConfiguration";
import GeneralConfiguration from "@/components/admin/tax-configs/GeneralConfiguration";
import TdsConfiguration from "@/components/admin/tax-configs/TdsConfiguration";
import LwfConfiguration from "@/components/admin/tax-configs/LwfConfiguration";
import {
  AlertTriangle,
  Building2,
  CreditCard,
  MapPin,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";

/* ---------------- Component ---------------- */

const TaxConfiguration = () => {
  const [activeTab, setActiveTab] = useState("epf");

  // State to track if config exists/is enabled (for view/edit flow)
  // ... (componentStates and other states remain the same)
  const [componentStates, setComponentStates] = useState({
    epf: false,
    esi: false,
    tds: false,
    professional_tax: false,
    lwf: false,
    statutory_bonus: false,
  });

  const [epfForm, setEpfForm] = useState(getDefaultEpfForm);
  const [esiForm, setEsiForm] = useState(getDefaultEsiForm);
  const [initialLoading, setInitialLoading] = useState(true);

  const { can } = usePermission();
  const [loading, setLoading] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [states, setStates] = useState([]); // For Professional Tax
  const [lwfConfig, setLwfConfig] = useState([]);
  const [viewMode, setViewMode] = useState({
    epf: false,
    esi: false,
    tds: false,
    professional_tax: false,
    lwf: false,
    statutory_bonus: false,
  });

  // Data loaded from backend for View mode
  const [epfView, setEpfView] = useState(null);
  const [esiView, setEsiView] = useState(null);

  const { canEdit, canCreate, canDelete } = createCanHelpers(can);

  const canEditTaxConfig = canEdit("tax_configuration");
  const tabs = [
    { id: "epf", label: "EPF" },
    { id: "esi", label: "ESI" },
    { id: "tds", label: "TDS" },
    { id: "professional_tax", label: "Professional Tax" },
    { id: "lwf", label: "Labour Welfare Fund" },
    // { id: "statutory_bonus", label: "Statutory Bonus" },
  ];

  const navigate = useNavigate();

  /* ---------------- Load config from backend ---------------- */
  const loadConfig = useCallback(
    async (type = activeTab) => {
      // We wrap the entire block in an initial load check
      // In a multi-tab view, we typically only fetch on initial load or explicitly on enable/save.
      // For this solution, we only load once to prevent the refresh flash on switch.
      // If data is already in view state, we skip loading.
      if (
        (type === "epf" && epfView && viewMode.epf) ||
        (type === "esi" && esiView && viewMode.esi) ||
        (type === "professional_tax" &&
          states.length > 0 &&
          viewMode.professional_tax)
      ) {
        return;
      }

      try {
        // Set loading for the specific component if its data is missing/stale
        setLoading(true);

        const response = await getTaxConfigurations(type);
        const data = response?.data;

        const isPt = type === "professional_tax";
        const isLwf = type === "lwf";

        if (isLwf) {
          console.log();
        }

        const isDataValidObject =
          !!data && typeof data === "object" && !Array.isArray(data);
        let hasData;

        if (isPt || isLwf) {
          // For PT/LWF (which return arrays of states)
          hasData = Array.isArray(data) && data.length > 0;
        } else if (isDataValidObject) {
          // For EPF, ESI, TDS (which return a single object)
          // Check if the object is non-empty AND explicitly enabled.
          hasData = Object.keys(data).length > 0 && data.enabled === true;
        } else {
          // Default case for null or non-object responses (e.g., empty 204 or non-existent entry)
          hasData = false;
        }
        // Update states and views based on fetched data
        if (!hasData) {
          setViewMode((prev) => ({ ...prev, [type]: false }));
          setComponentStates((prev) => ({ ...prev, [type]: false }));
          if (type === "epf") setEpfView(null);
          if (type === "esi") setEsiView(null);
          if (isPt) setStates([]);
          if (isLwf) setLwfConfig([]);
          return;
        }

        setViewMode((prev) => ({ ...prev, [type]: true }));
        setComponentStates((prev) => ({ ...prev, [type]: true }));

        if (type === "epf") {
          setEpfView(data);
          setEpfForm(mapEpfFromServer(data));
        }
        if (type === "esi") {
          setEsiView(data);
          setEsiForm(mapEsiFromServer(data));
        }
        if (isPt) {
          setStates(data);
        }
        if (isLwf) {
          // <--- HANDLE LWF DATA
          setLwfConfig(data);
        }
      } catch (error) {
        console.error(`Failed to load config for ${type}:`, error);
      } finally {
        setLoading(false);
      }
    },
    [
      epfView,
      esiView,
      states.length,
      viewMode.epf,
      viewMode.esi,
      viewMode.professional_tax,
    ]
  );

  // OPTIMIZATION: Load all configs on initial mount, NOT on every tab switch
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      if (isMounted) setInitialLoading(true);
      try {
        await Promise.all(tabs.map((tab) => loadConfig(tab.id)));
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };
    init();
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array means this runs only once

  /* ---------------- Enable/Disable/Save handlers (Simplified for brevity) ---------------- */

  const handleEnable = (component) => {
    if (!canCreate("tax_configuration", true)) return;

    if (component === "professional_tax") {
      navigate("/settings", { state: { tab: "org-structure" } });
      return;
    }
    setViewMode((prev) => ({ ...prev, [component]: false }));
    if (component === "epf") {
      setEpfForm(getDefaultEpfForm());
    }
    if (component === "esi") {
      setEsiForm(getDefaultEsiForm());
    }
    setComponentStates((prev) => ({ ...prev, [component]: true }));
  };

  const handleCancel = async () => {
    // Reloads ONLY the active tab's config to revert local form state
    await loadConfig(activeTab);

    // If there is no saved config, ensure the view reverts to the disabled state
    const hasConfig =
      (activeTab === "epf" && epfView) ||
      (activeTab === "esi" && esiView) ||
      (activeTab === "professional_tax" && states.length > 0);
    if (!hasConfig) {
      setComponentStates((prev) => ({ ...prev, [activeTab]: false }));
      setViewMode((prev) => ({ ...prev, [activeTab]: false }));
    }
  };

  const handleDisable = async (component) => {
    if (!canDelete("tax_configuration", true)) return;

    try {
      setSavingSection(component);
      await disableTaxConfiguration(component);
      setComponentStates((prev) => ({ ...prev, [component]: false }));
      setViewMode((prev) => ({ ...prev, [component]: false }));
      if (component === "epf") {
        setEpfForm(getDefaultEpfForm());
        setEpfView(null);
      }
      if (component === "esi") {
        setEsiForm(getDefaultEsiForm());
        setEsiView(null);
      }
      if (component === "professional_tax") {
        setStates([]);
      }
    } catch (error) {
      console.error("Failed to disable section:", error);
    } finally {
      setSavingSection(null);
    }
  };

  const handleSave = async (section) => {
    try {
      if (!canEdit("tax_configuration", true)) return;
      setSavingSection(section);
      let payload = { enabled: true };

      if (section === "epf") {
        if (!epfForm.epf_number) {
          toast.error("EPF Number is required!");
          return;
        }
        payload = { ...epfForm, enabled: true };
      } else if (section === "esi") {
        const fields = {
          "ESI Number": esiForm.esi_number,
          "Employee Contribution": esiForm.employeeContribution,
          "Employer Contribution": esiForm.employerContribution,
        };

        for (const [label, value] of Object.entries(fields)) {
          if (value === undefined || value === null || value === "") {
            toast.error(`${label} is required!`);
            return;
          }
        }

        payload = { ...esiForm, enabled: true };
      }

      if (section === "epf" || section === "esi") {
        const updated = await createTaxConfiguration(payload, section);
        setComponentStates((prev) => ({
          ...prev,
          [section]: !!updated?.enabled,
        }));
        setViewMode((prev) => ({ ...prev, [section]: true }));

        if (section === "epf") setEpfForm(mapEpfFromServer(updated));
        if (section === "esi") setEsiForm(mapEsiFromServer(updated));
      }

      await loadConfig(section); // Re-run loadConfig to ensure view is updated
    } catch (error) {
      console.error("Error saving section:", error);
    } finally {
      setSavingSection(null);
    }
  };

  // Utility function to determine if a component should show its disabled state
  const shouldShowDisabledState = (type) => {
    const hasConfig =
      (type === "epf" && epfView) ||
      (type === "esi" && esiView) ||
      (type === "professional_tax" && states.length > 0);

    // If Professional Tax has no states, always show disabled state first (unless in editor)
    if (type === "professional_tax" && states.length === 0) return true;

    // If config exists, it shouldn't show disabled state
    if (hasConfig) return false;

    // For others, if the state is not enabled and config doesn't exist, show disabled
    return !hasConfig && !componentStates[type];
  };

  // --- RENDERING ALL COMPONENTS (The fix for smooth switching) ---
  const renderAllTabContent = () => (
    <div className="relative pt-2">
      {tabs.map((tab) => {
        const isVisible = activeTab === tab.id;

        // Determine content based on type and current state
        let content;

        if (shouldShowDisabledState(tab.id)) {
          content = (
            <GeneralConfiguration
              activeTab={tab.id}
              handleEnable={handleEnable}
              tabs={tabs}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        } else if (tab.id === "epf") {
          content = (
            <EpfConfiguration
              epfForm={epfForm}
              setEpfForm={setEpfForm}
              epfView={epfView}
              isViewMode={viewMode.epf}
              setViewMode={setViewMode}
              handleSave={handleSave}
              handleCancel={handleCancel}
              handleDisable={handleDisable}
              savingSection={savingSection}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        } else if (tab.id === "esi") {
          content = (
            <EsiConfiguration
              esiForm={esiForm}
              setEsiForm={setEsiForm}
              esiView={esiView}
              isViewMode={viewMode.esi}
              setViewMode={setViewMode}
              handleSave={handleSave}
              handleCancel={handleCancel}
              handleDisable={handleDisable}
              savingSection={savingSection}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        } else if (tab.id === "professional_tax") {
          content = (
            <ProfessionalTaxConfiguration
              states={states}
              isViewMode={viewMode.professional_tax}
              setViewMode={setViewMode}
              loadConfig={loadConfig}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        } else if (tab.id === "lwf") {
          // <--- NEW LWF ROUTING
          content = (
            <LwfConfiguration
              lwfConfig={lwfConfig}
              handleEnable={handleEnable} // Need specific implementation in parent later
              handleDisable={handleDisable} // Need specific implementation in parent later
              savingSection={savingSection}
              refreshLwfConfig={refreshLwfConfig}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        } else if (tab.id === "tds") {
          // <-- NEW TDS INTEGRATION
          content = (
            <TdsConfiguration
              handleCancel={handleCancel}
              savingSection={savingSection}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        } else {
          // TDS, LWF, Statutory Bonus (Placeholders)
          content = (
            <GeneralConfiguration
              activeTab={tab.id}
              handleSave={handleSave}
              handleCancel={handleCancel}
              handleDisable={handleDisable}
              savingSection={savingSection}
              tabs={tabs}
              canEditTaxConfig={canEditTaxConfig}
            />
          );
        }

        return (
          <div
            key={tab.id}
            role="tabpanel"
            hidden={!isVisible} // This CSS hides the content without destroying the component state
            className={`transition-opacity duration-300 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            {/* Render the specific content inside the hidden/visible container */}
            {content}
          </div>
        );
      })}
    </div>
  );

  const refreshLwfConfig = async () => {
    await loadConfig("lwf");
  };

  if (initialLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="rounded-xl border p-6 space-y-3">
          <div className="flex gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-24" />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // --- Summary Helper Flags ---
  const epfConfigured = epfView && epfView.enabled;
  const esiConfigured = esiView && esiView.enabled;
  const ptConfigured = states.length > 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Tax Configuration
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Configure statutory components for payroll compliance
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
      {/* PF – Employee + Employer */}
      <Card className={`shadow-sm hover:shadow-md transition-all duration-200 ${!epfConfigured ? 'border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50' : 'border-primary/30 dark:border-primary/50'}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <Shield className={`h-10 w-10 ${epfConfigured ? 'text-[#3194A0]' : 'text-gray-400'}`} />

            <div className="space-y-1">
              <p className="text-sm text-gray-500">PF Contribution</p>

              <div className="flex flex-col leading-tight">
                {!epfConfigured ? (
                  <div className="flex items-center space-x-1 text-red-600 dark:text-red-400 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Not Configured</span>
                  </div>
                ) : (
                  <>
                    <span className="font-semibold text-gray-800 dark:text-gray-300">
                      Employee:{" "}
                      <span className="text-[#3194A0]">
                        {epfView?.employee_contribution_rate
                        ? epfView.employee_contribution_rate.split("_")[0]
                        : "-"}
                      %                      </span>
                    </span>

                    <span className="font-semibold text-gray-800 dark:text-gray-300">
                      Employer:{" "}
                      <span className="text-[#3194A0]">
                        {epfView?.employer_contribution_rate
                        ? epfView.employer_contribution_rate.split("_")[0]
                        : "-"}
                      %
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ESI – Employee + Employer */}
      <Card className={`shadow-sm hover:shadow-md transition-all duration-200 ${!esiConfigured ? 'border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50' : 'border-primary/30 dark:border-primary/50'}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <CreditCard className={`h-10 w-10 ${esiConfigured ? 'text-primary' : 'text-gray-400'}`} />

            <div className="space-y-1">
              <p className="text-sm text-gray-500">ESI Contribution</p>

              <div className="flex flex-col leading-tight">
                {!esiConfigured ? (
                  <div className="flex items-center space-x-1 text-red-600 dark:text-red-400 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Not Configured</span>
                  </div>
                ) : (
                  <>
                    <span className="font-semibold text-gray-800 dark:text-gray-300">
                      Employee:{" "}
                      <span className="text-[#3194A0]">
                        {esiForm?.employeeContribution
                        ? esiForm.employeeContribution
                        : "-"}
                      %
                      </span>
                    </span>

                    <span className="font-semibold text-gray-800 dark:text-gray-300">
                      Employer:{" "}
                      <span className="text-[#3194A0]">
                         {esiForm?.employerContribution
                        ? esiForm.employerContribution
                        : "-"}
                      %
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PT - States Configured */}
      <Card className={`shadow-sm hover:shadow-md transition-all duration-200 ${!ptConfigured ? 'border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50' : 'border-primary/30 dark:border-primary/50'}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <MapPin className={`h-10 w-10 ${ptConfigured ? 'text-orange-600' : 'text-gray-400'}`} />

            <div className="space-y-1">
              <p className="text-sm text-gray-500">States Configured</p>

              <div className="flex flex-col leading-tight">
                {!ptConfigured ? (
                  <div className="flex items-center space-x-1 text-red-600 dark:text-red-400 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Not Configured</span>
                  </div>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-gray-800 dark:text-gray-300 leading-none">
                      {states.length}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

      {/* Custom Horizontal Tabs - Text with Underline */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative pb-3 text-sm font-medium transition-colors duration-200
                ${
                  activeTab === tab.id
                    ? "text-primary dark:text-primary"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                }
              `}
            >
              {tab.label}
              <span
                className={`
                  absolute bottom-0 left-0 w-full h-0.5 bg-primary dark:bg-primary
                  transition-all duration-300 ease-in-out
                  ${
                    activeTab === tab.id
                      ? "opacity-100 scale-x-100"
                      : "opacity-0 scale-x-0"
                  }
                `}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content - Now renders all tabs, preserving state */}
      {renderAllTabContent()}
    </div>
  );
};

export default TaxConfiguration;
